import { Audience } from './types';
import { createListItem, resetSearchResult, createAddedItem } from './dom-helpers';
import { showSubPage, setActiveStep } from './steps';
import { updateSearchParams, resetSearchParams, commitField, clearField, audienceParamsForSave } from './search-params';
import {
  MATERIAL_FORMATS,
  materialIdAttr,
  loadMaterials,
  clearMaterialPreview,
  clearUploadErrors,
  initMaterialPanel,
} from './materials';
import { populateReview } from './review';
import { getGeoLocations, getSpecialAdCountries, submitCreateCampaign } from './campaign-submit';
import { setAudienceParams, getAudienceParams, clearAudienceData } from './audience-params-store';
import { initAssigmentDropdowns, resetAssignment, refreshAssignmentData } from './assign_user';
import { initGeoLocationDropdowns, refreshGeoSummaries } from '../geo_locations';
import { bindScheduleWindow, syncScheduleWindow } from '../schedule_window';
import { InfiniteChoices, ChoiceRecord } from './infinite_choices';
import { createChoicesDropdownOverlay } from './choices_overlay';

export * from './types';
export { updateSearchParams } from './search-params';
export { destroyAssignmentDropdowns } from './assign_user';

let audienceInf: InfiniteChoices | null = null;
let audienceSelectEl: HTMLSelectElement | null = null;
const audienceOverlay = createChoicesDropdownOverlay(() => audienceSelectEl);
let candidatesCount: number | null = null;

function renderField($row: JQuery): void {
  const $items = $row.find('.ads-pool-added-items');
  if (!$items.length) return;
  const $container = $row.find('.ads-pool-add-container');
  const $input = $container.find('.ads-pool-add-input');
  const $addBtn = $container.find('.ads-pool-add-button');

  $items.empty();
  $input.hide();

  const $keywords = $row.find('[data-keywords]');
  if ($keywords.length) {
    const value = String($keywords.val() ?? '').trim();
    if (!value) {
      $addBtn.show();
      return;
    }
    $addBtn.hide();
    const $item = createAddedItem(value, 'far fa-edit');
    $item.on('click', function() {
      $addBtn.hide();
      $items.empty();
      $input.show();
      $keywords.trigger('focus');
    });
    $items.append($item);
    return;
  }

  if ($row.find('[data-range]').length) {
    $addBtn.show();
    const min = String($row.find('[data-range="min"]').val() ?? '').trim();
    const max = String($row.find('[data-range="max"]').val() ?? '').trim();
    if (min === '' || max === '') return;
    const $item = createAddedItem(`${min} - ${max}`, 'far fa-minus-circle');
    $item.on('click', function() {
      clearField($row);
      $items.empty();
      resetSearchResult();
    });
    $items.append($item);
  }
}

function addListItem($row: JQuery): void {
  const $container = $row.find('.ads-pool-add-container');
  const $textInput = $container.find('[data-text]');
  const text = String($textInput.val() ?? '').trim();
  if (!text) { $textInput.trigger('focus'); return; }

  if ($row.data('filter') === 'companies'
      && $row.find('.ads-pool-added-items .ads-pool-added-item').length >= 20) {
    window.alert('Maximum 20 companies');
    return;
  }

  const $checked = $container.find('.ads-pool-add-options input[type="radio"]:checked');
  const filter = $checked.length ? String($checked.val()) : null;
  const filterLabel = $checked.length ? $checked.closest('label').text().trim() : null;

  appendListItem($row, text, filter, filterLabel);
  $textInput.val('').trigger('focus');
  updateSearchParams();
  resetSearchResult();
}

function appendListItem($row: JQuery, text: string, filter: string | null, filterLabel: string | null): void {
  const $item = createListItem(text, filter, filterLabel);
  $item.on('click', function() {
    $(this).remove();
    updateSearchParams();
    resetSearchResult();
  });
  $row.find('.ads-pool-added-items').append($item);
}

function ensureAudienceInfinite(): InfiniteChoices | null {
  const el = $('.audience-select')[0] as HTMLSelectElement | undefined;
  if (!el) return null;

  if (audienceInf && (audienceSelectEl !== el || audienceInf.isStale())) {
    destroyAudienceInfinite();
  }

  if (!audienceInf) {
    audienceSelectEl = el;
    audienceInf = new InfiniteChoices({
      el,
      url: '/audiences',
      placeholder: 'Choose an audience...',
      searchPlaceholder: 'Search audiences…',
      buildLabel: a => (a.audience_count != null ? `${a.name} (${a.audience_count} candidates)` : a.name),
      choicesConfig: { position: 'bottom' },
      onShowDropdown: audienceOverlay.show,
      onHideDropdown: audienceOverlay.hide,
      autoLoad: false,
    });
    audienceInf.init();
  }
  return audienceInf;
}

function destroyAudienceInfinite(): void {
  if (audienceInf) {
    audienceOverlay.hide();
    audienceInf.destroy();
    audienceInf = null;
    audienceSelectEl = null;
  }
}

function loadAudiences(selectId?: string): void {
  const inf = ensureAudienceInfinite();
  if (!inf) return;

  if (selectId) {
    const name = $('#ad-campaign-modal-body').attr('data-audience-name') || '';
    inf.selectRecord({ id: selectId, name, search_params: getAudienceParams() } as ChoiceRecord);
  } else {
    inf.reload();
  }
}

function applyAdSetGeoRestriction(): void {
  const allowed = new Set(
    $('#ad-campaign-info-step [data-role="campaign-geo-location"] input[type="checkbox"]:checked')
      .map((_i, el) => (el as HTMLInputElement).value)
      .get()
  );

  $('#ad-campaign-info-step [data-role="campaign-adset-geo-location"] input[type="checkbox"]')
    .each((_i, el) => {
      const cb = el as HTMLInputElement;
      const isAllowed = allowed.has(cb.value);
      cb.disabled = !isAllowed;
      if (!isAllowed) cb.checked = false;
    });

  refreshGeoSummaries();
}

function restoreAudienceStep(): void {
  const storedId = $('#ad-campaign-modal-body').attr('data-audience-id');
  if (!storedId) return;

  if ($('#existing-audience-card').hasClass('active-selection')) {
    loadAudiences(storedId);
  } else if ($('#new-audience-card').hasClass('active-selection')) {
    if ($('.audiences-search-result').attr('data-count')) $('#next-btn').addClass('save-audience');
  }
}

export function initAdCampaign() {
  if (!$('#campaignModal').length) return;

  function resetCampaignModal($modal: JQuery): void {
    $modal.find('#existing-audience-section, #new-audience-section, #audience-name-section').addClass('d-none');
    $modal.find('#existing-audience-card, #new-audience-card').removeClass('active-selection');
    $modal.find('.audiences-search-result').removeClass('text-danger').removeAttr('data-count').empty();
    $modal.find('.audiences-search-button').show();
    $modal.find('.filter-row .ads-pool-added-items').empty();
    $modal.find('.filter-row .ads-pool-add-input').hide();
    $modal.find('.filter-row .ads-pool-add-button').show();
    $modal.find('.filter-row [data-range], .filter-row [data-keywords], .filter-row [data-text]').each(function() {
      (this as HTMLInputElement | HTMLTextAreaElement).value = (this as HTMLInputElement | HTMLTextAreaElement).defaultValue;
    });
    $modal.find('.filter-row .ads-pool-add-options input[type="radio"]').each(function() {
      (this as HTMLInputElement).checked = (this as HTMLInputElement).defaultChecked;
    });
    $modal.find('#audience-name-input').val('');
    destroyAudienceInfinite();
    resetSearchParams();
    showSubPage('#ad-audience-step');
    setActiveStep('#audience-step');
    $('#next-btn').removeClass('save-audience ad-materials').text('Next');
    $('#back-btn').addClass('d-none');

    $modal.find('.material-panel').each(function() {
      const $panel = $(this);
      $panel.find('.material-tab').removeClass('active');
      $panel.find('.material-tab[data-tab="library"]').addClass('active');
      $panel.find('.material-tab-pane').addClass('d-none');
      $panel.find('[data-tab-pane="library"]').removeClass('d-none');
      $panel.find('[data-role="material-name"]').val('');
      const fileInput = $panel.find('[data-role="file-input"]')[0] as HTMLInputElement | undefined;
      if (fileInput) fileInput.value = '';
      $panel.find('[data-role="materials-grid"]').empty();
      $panel.find('[data-role="materials-pagination"]').empty();
      clearMaterialPreview($panel);
      clearUploadErrors($panel);
    });
    MATERIAL_FORMATS.forEach(fmt => $('#ad-campaign-modal-body').removeAttr(materialIdAttr(fmt)));
    clearAudienceData();
    resetAssignment();
    $modal.find('[data-role="summary-audience"]').html('<span class="text-muted">No audience selected</span>');
    $modal.find('[data-role="summary-materials"]').empty();
    $modal.find('[data-role="summary-campaign-info"]').empty();
    $modal.find('[data-role="summary-assignment"]').html('<span class="text-muted">Not assigned</span>');

    const campaignForm = $modal.find('form.campaign-form')[0] as HTMLFormElement | undefined;
    if (campaignForm) campaignForm.reset();
    syncScheduleWindow('#campaign-start-date', '#campaign-end-date');
    refreshGeoSummaries();
    applyAdSetGeoRestriction();
  }

  $('#campaignModal')
    .off('show.bs.modal.resetSubPages hidden.bs.modal.resetSubPages')
    .on('show.bs.modal.resetSubPages hidden.bs.modal.resetSubPages', function() {
      resetCampaignModal($(this));
    });

  $('#existing-audience-card').off('click').on('click', function() {
    $(this).addClass('active-selection');
    $('#new-audience-card').removeClass('active-selection');
    $('#existing-audience-section').removeClass('d-none');
    $('#new-audience-section').addClass('d-none');
    loadAudiences();
  });

  $('#new-audience-card').off('click').on('click', function() {
    $(this).addClass('active-selection');
    $('#existing-audience-card').removeClass('active-selection');
    $('#existing-audience-section').addClass('d-none');
    $('#new-audience-section').removeClass('d-none');
    $('#next-btn').addClass('save-audience');
  });

  $(document).off('click.listAdd').on('click.listAdd', '.filter-row .list-add-btn', function() {
    addListItem($(this).closest('.filter-row'));
  });

  $(document).off('keypress.listAdd').on('keypress.listAdd', '.filter-row [data-text]', function(e) {
    if ((e as JQuery.KeyPressEvent).which !== 13) return;
    e.preventDefault();
    addListItem($(this).closest('.filter-row'));
  });

  $(document).off('click.filterReveal').on('click.filterReveal', '.filter-row .ads-pool-add-button', function() {
    const $container = $(this).closest('.ads-pool-add-container');
    $(this).hide();
    $container.find('.ads-pool-add-input').show();
    $container.find('[data-text], [data-keywords], [data-range="min"]').first().trigger('focus');
  });

  $(document).off('mousedown.filterCollapse').on('mousedown.filterCollapse', function(e) {
    const target = e.target as Node;
    $('.filter-row .ads-pool-add-container').each(function() {
      const container = this as HTMLElement;
      const $container = $(container);
      if (!$container.find('[data-text]').length) return;
      const $input = $container.find('.ads-pool-add-input');
      if (!$input.is(':visible')) return;
      if (container === target || container.contains(target)) return;
      $input.hide();
      $container.find('.ads-pool-add-button').show();
    });
  });

  $(document).off('click.filterSave').on('click.filterSave', '.filter-row .save-btn', function() {
    const $row = $(this).closest('.filter-row');
    commitField($row);
    renderField($row);
    resetSearchResult();
  });

  $(document).off('click.filterDelete').on('click.filterDelete', '.filter-row .delete-btn', function() {
    const $row = $(this).closest('.filter-row');
    $row.find('[data-keywords]').val('');
    clearField($row);
    renderField($row);
    resetSearchResult();
  });

  $('.audiences-search-button').off('click').on('click', function() {
    const $btn = $(this);
    const $result = $('.audiences-search-result');
    const params = updateSearchParams();
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';

    const originalHtml = $btn.html();
    $btn.prop('disabled', true).removeClass('btn btn-primary').addClass('is-loading').html(
      '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> &nbsp; Searching candidates...'
    );
    $result.removeClass('text-danger').text('');

    $.ajax({
      url: '/ads_pool_search',
      method: 'POST',
      contentType: 'application/json',
      headers: { 'X-CSRF-Token': csrfToken },
      data: JSON.stringify({ data: params }),
      success: function(response: { results_count?: number; error?: string }) {
        if (response.error) {
          $result.addClass('text-danger').text(response.error);
        } else {
          const count = Number(response.results_count) || 0;
          candidatesCount = count;
          $result.attr('data-count', String(count))
            .html(`<i class="fal fa-check text-success"></i> &nbsp; <strong>${count.toLocaleString()} prospects found</strong>`);
          $btn.hide();
          $('#audience-name-section').removeClass('d-none');
          $('#next-btn').addClass('save-audience');
        }
      },
      error: function(xhr) {
        const msg = xhr.responseJSON?.error ?? 'Search failed';
        $result.addClass('text-danger').text(msg);
      },
      complete: function() {
        $btn.prop('disabled', false).addClass('btn btn-primary').removeClass('is-loading').html(originalHtml);
      },
    });
  });

  $('#next-btn').off('click').on('click', function() {
    const btn = $(this);

    const advanceToMaterials = () => {
      showSubPage('#ad-materials-step');
      setActiveStep('#materials-step');
      btn.removeClass('save-audience').addClass('ad-materials');
      $('#back-btn').removeClass('d-none');
      MATERIAL_FORMATS.forEach(fmt => loadMaterials(fmt, 1));
    };

    if (!$('#ad-audience-step').hasClass('d-none')) {
      const selectedAudienceId = audienceInf?.getSelectedId() || '';
      const isExistingCardActive = $('#existing-audience-card').hasClass('active-selection');
      const isNewCardActive = $('#new-audience-card').hasClass('active-selection');

      if (selectedAudienceId) {
        const cached = audienceInf?.getSelected();
        const audienceLabel = cached?.name || selectedAudienceId;
        $('#ad-campaign-modal-body').attr('data-audience-id', selectedAudienceId);
        $('#ad-campaign-modal-body').attr('data-audience-name', audienceLabel);
        setAudienceParams(cached?.search_params ?? {});
        advanceToMaterials();
        return;
      }

      if (!isExistingCardActive && !isNewCardActive) {
        window.alert('Please choose an existing audience or create a new one before continuing.');
        return;
      }

      if (isExistingCardActive && !selectedAudienceId) {
        window.alert('Please select an audience from the list before continuing.');
        return;
      }

      if (isNewCardActive && !btn.hasClass('save-audience')) {
        window.alert('Please run the search and name your audience before continuing.');
        return;
      }

      if (btn.hasClass('save-audience')) {
        const audienceName = $('#audience-name-input').val()?.toString().trim();
        if (!audienceName) {
          window.alert('Please enter a name for the audience before continuing.');
          return;
        }
        const count = parseInt($('.audiences-search-result').attr('data-count') ?? '', 10);
        const candidates_count = Number.isFinite(count) ? count : 0;

        const currentParams = audienceParamsForSave(updateSearchParams());

        const savedAudienceId = $('#ad-campaign-modal-body').attr('data-audience-id');
        const savedName = $('#ad-campaign-modal-body').attr('data-audience-name');
        const unchanged = !!savedAudienceId
          && savedName === audienceName
          && JSON.stringify(getAudienceParams()) === JSON.stringify(currentParams);

        if (unchanged) {
          advanceToMaterials();
          return;
        }

        const originalNextHtml = btn.html();
        btn.prop('disabled', true).html(
          '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> &nbsp; Saving...'
        );
        $('#back-btn').prop('disabled', true);

        $.ajax({
          url: savedAudienceId ? `/audiences/${savedAudienceId}` : '/ads_pool_search/save_audience',
          method: savedAudienceId ? 'PATCH' : 'POST',
          contentType: 'application/json',
          headers: { 'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '' },
          data: JSON.stringify({ name: audienceName, data: currentParams, candidates_count: candidates_count }),
          success: function(response: { success: boolean; error?: string; audience_id: string }) {
            if (response.success) {
              window.alert('Audience saved successfully');
              $('#ad-campaign-modal-body').attr('data-audience-id', response.audience_id);
              $('#ad-campaign-modal-body').attr('data-audience-name', audienceName);
              setAudienceParams(currentParams);
              advanceToMaterials();
            } else {
              window.alert(response.error ?? 'Failed to save audience');
            }
          },
          error: function() {
            window.alert('An error occurred while saving the audience');
          },
          complete: function() {
            btn.prop('disabled', false).html(originalNextHtml);
            $('#back-btn').prop('disabled', false);
          },
        });
      }
      return;
    }

    if (!$('#ad-materials-step').hasClass('d-none')) {
      const missing = MATERIAL_FORMATS.filter(fmt => !$('#ad-campaign-modal-body').attr(materialIdAttr(fmt)));
      if (missing.length) {
        const labels = missing.map(f => f === 'square' ? 'Square' : 'Story').join(' and ');
        window.alert(`Please select or upload a ${labels} material before continuing.`);
        return;
      }
      showSubPage('#ad-campaign-info-step');
      setActiveStep('#campaign-info-step');
      btn.removeClass('ad-materials');
      return;
    }

    if (!$('#ad-campaign-info-step').hasClass('d-none')) {
      const $form = $('#ad-campaign-info-step form.campaign-form')[0] as HTMLFormElement | undefined;
      if ($form && typeof $form.reportValidity === 'function' && !$form.reportValidity()) return;
      const countriesList = getSpecialAdCountries();
      if (countriesList.length === 0) {
        window.alert('Select at least one special ad country.');
        return;
      }
      const adsetGeoLocations = getGeoLocations();
      if (adsetGeoLocations.length === 0) {
        window.alert('Select at least one ad set geographic location.');
        return;
      }
      showSubPage('#assign-user-step');
      setActiveStep('#assign-step');
      refreshAssignmentData();
      return;
    }

    if (!$('#assign-user-step').hasClass('d-none')) {
      populateReview();
      showSubPage('#ad-review-step');
      setActiveStep('#summary-step');
      btn.text('Create Campaign');
      return;
    }

    if (!$('#ad-review-step').hasClass('d-none')) {
      submitCreateCampaign(btn);
      return;
    }
  });

  $('#ad-campaign-modal-body .material-panel').each(function() { initMaterialPanel($(this)); });

  $('#back-btn').off('click').on('click', function() {
    const $btn = $(this);

    if (!$('#ad-review-step').hasClass('d-none')) {
      showSubPage('#assign-user-step');
      setActiveStep('#assign-step');
      $('#next-btn').text('Next');
      refreshAssignmentData();
      return;
    }

    if (!$('#assign-user-step').hasClass('d-none')) {
      showSubPage('#ad-campaign-info-step');
      setActiveStep('#campaign-info-step');
      return;
    }

    if (!$('#ad-campaign-info-step').hasClass('d-none')) {
      showSubPage('#ad-materials-step');
      setActiveStep('#materials-step');
      $('#next-btn').addClass('ad-materials');
      return;
    }

    if (!$('#ad-materials-step').hasClass('d-none')) {
      showSubPage('#ad-audience-step');
      setActiveStep('#audience-step');
      $btn.addClass('d-none');
      $('#next-btn').removeClass('ad-materials');
      restoreAudienceStep();
      return;
    }
  });

  $(document).off('click.editStep').on('click.editStep', '#ad-review-step [data-edit-step]', function() {
    const target = ($(this).data('edit-step') as string) ?? '';
    $('#next-btn').text('Next');
    if (target === 'audience') {
      showSubPage('#ad-audience-step');
      setActiveStep('#audience-step');
      $('#back-btn').addClass('d-none');
      $('#next-btn').removeClass('ad-materials');
      restoreAudienceStep();
      return;
    }
    if (target === 'materials') {
      showSubPage('#ad-materials-step');
      setActiveStep('#materials-step');
      $('#back-btn').removeClass('d-none');
      $('#next-btn').addClass('ad-materials');
      return;
    }
    if (target === 'campaign-info') {
      showSubPage('#ad-campaign-info-step');
      setActiveStep('#campaign-info-step');
      $('#back-btn').removeClass('d-none');
      $('#next-btn').removeClass('ad-materials');
      return;
    }
    if (target === 'assign') {
      showSubPage('#assign-user-step');
      setActiveStep('#assign-step');
      $('#back-btn').removeClass('d-none');
      $('#next-btn').removeClass('ad-materials');
      refreshAssignmentData();
      return;
    }
  });

  $(document)
    .off('change.campaignAdSetGeo')
    .on('change.campaignAdSetGeo',
      '#ad-campaign-info-step [data-role="campaign-geo-location"] input[type="checkbox"]',
      applyAdSetGeoRestriction);

  initAssigmentDropdowns();
  initGeoLocationDropdowns();
  bindScheduleWindow('campaignSchedule', '#campaign-start-date', '#campaign-end-date');
  applyAdSetGeoRestriction();
}
